import { env } from '../env';
import { ScheduleDestinationInput, SocialAccountItem, SocialPostScheduleItem } from '../types/social';

const BASE_URL = env.apiUrl;

function authHeaders(accessToken: string): HeadersInit {
  return { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
}

// Each function takes `accessToken` as its first argument so it can be passed directly
// to useApiCallWithAuth(fn), which calls fn(accessToken, ...args) after minting the token.

export async function listSocialAccounts(accessToken: string): Promise<SocialAccountItem[]> {
  const res = await fetch(`${BASE_URL}/api/v1/social/accounts`, { headers: authHeaders(accessToken) });
  if (!res.ok) throw new Error('Failed to load social accounts');
  return res.json();
}

export async function disconnectSocialAccount(accessToken: string, socialAccountGuid: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/v1/social/accounts/${socialAccountGuid}/disconnect`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error('Failed to disconnect account');
}

export async function listSchedulesForBlog(accessToken: string, blogId: number): Promise<SocialPostScheduleItem[]> {
  const res = await fetch(`${BASE_URL}/api/v1/social/blogs/${blogId}/schedules`, { headers: authHeaders(accessToken) });
  if (!res.ok) throw new Error('Failed to load scheduled posts');
  return res.json();
}

export async function upsertSchedulesForBlog(
  accessToken: string,
  blogId: number,
  destinations: ScheduleDestinationInput[]
): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/v1/social/blogs/${blogId}/schedules`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ destinations }),
  });
  if (!res.ok) throw new Error('Failed to save schedule');
}

export interface SchedulePatch {
  scheduledFor?: string;
  messageOverride?: string;
  status?: 'Canceled';
}

export async function updateSchedule(
  accessToken: string,
  socialPostScheduleGuid: string,
  patch: SchedulePatch
): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/v1/social/schedules/${socialPostScheduleGuid}`, {
    method: 'PATCH',
    headers: authHeaders(accessToken),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error('Failed to update scheduled post');
}

// Plain browser navigations (OAuth needs a real redirect) — no bearer token needed or possible here.
export function getLinkedInConnectUrl(): string {
  return `${BASE_URL}/api/v1/social/connect/linkedin`;
}

export function getFacebookConnectUrl(): string {
  return `${BASE_URL}/api/v1/social/connect/facebook`;
}
